import { useState, useMemo } from "react";
import { Check, X, Info } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateProductSchema, generateFAQSchema, generateBreadcrumbSchema } from "@/lib/jsonld";
import faviconGratuit from "@/assets/pricing/favicon-gratuit.png";
import faviconStarter from "@/assets/pricing/favicon-starter.png";
import faviconPro from "@/assets/pricing/favicon-pro.png";
interface Feature {
  text: string;
  included: boolean;
  tooltip?: string;
}
interface PricingPlan {
  name: string;
  subtitle: string;
  monthlyPrice: number;
  annualPrice: number;
  priceLabel: string;
  annualPriceLabel?: string;
  features: Feature[];
  buttonText: string;
  helperText: string;
  favicon: string;
  popular?: boolean;
}
const plans: PricingPlan[] = [{
  name: "Gratuit",
  subtitle: "Formule de base avec fonctionnalités limitées",
  monthlyPrice: 0,
  annualPrice: 0,
  priceLabel: "à vie",
  features: [{
    text: "1 carte au total",
    included: true,
    tooltip: "La possibilité de créer une carte Wallet unique avec son propre design, logo et contenu."
  }, {
    text: "Jusqu'à 100 installations (Apple & Google Wallet)",
    included: true
  }, {
    text: "4 diffusions / mois",
    included: true,
    tooltip: "1 diffusion = 1 campagne de notification push, quel que soit le nombre d'abonnés qui la reçoivent."
  }, {
    text: "Gestion basique de la carte",
    included: true
  }, {
    text: "Pas de code d'intégration",
    included: false
  }, {
    text: "Pas de notifications géolocalisées",
    included: false
  }],
  buttonText: "Commencer gratuitement",
  helperText: "Sans carte bancaire",
  favicon: faviconGratuit
}, {
  name: "Starter",
  subtitle: "Pour les créateurs de contenu, coachs et commerces de proximité",
  monthlyPrice: 29,
  annualPrice: 23,
  priceLabel: "/ mois",
  annualPriceLabel: "Facturé annuellement",
  features: [{
    text: "Jusqu'à 2 500 installations (Apple & Google Wallet)",
    included: true
  }, {
    text: "100 diffusions / mois",
    included: true,
    tooltip: "1 diffusion = 1 campagne de notification push, quel que soit le nombre d'abonnés qui la reçoivent."
  }, {
    text: "1 carte au total",
    included: true,
    tooltip: "La possibilité de créer une carte Wallet unique avec son propre design, logo et contenu."
  }, {
    text: "Code d'intégration (embed)",
    included: true,
    tooltip: "Intégrez votre carte directement sur votre site ou application, offrant ainsi une expérience entièrement personnalisée."
  }, {
    text: "Notifications géolocalisées",
    included: true,
    tooltip: "Déclenchez automatiquement une notification push lorsqu'un abonné entre dans une zone définie."
  }, {
    text: "Tags & segmentation",
    included: true,
    tooltip: "Ciblez des groupes spécifiques d'abonnés avec des notifications personnalisées basées sur vos tags et segments."
  }, {
    text: "Statistiques & analyses détaillées",
    included: true,
    tooltip: "Suivez les performances de votre carte, installations, taux de clics et résultats de vos diffusions."
  }, {
    text: "Retirez la marque Unotif",
    included: true
  }],
  buttonText: "Continuer avec Starter",
  helperText: "Sans engagement",
  favicon: faviconStarter,
  popular: true
}, {
  name: "Pro",
  subtitle: "Pour les équipes marketing, agences et e-commerces en forte croissance",
  monthlyPrice: 89,
  annualPrice: 71,
  priceLabel: "/ mois",
  annualPriceLabel: "Facturé annuellement",
  features: [{
    text: "Jusqu'à 25 000 installations (Apple & Google Wallet)",
    included: true
  }, {
    text: "1 000 diffusions / mois",
    included: true,
    tooltip: "1 diffusion = 1 campagne de notification push, quel que soit le nombre d'abonnés qui la reçoivent."
  }, {
    text: "Jusqu'à 10 cartes au total",
    included: true,
    tooltip: "Créez plusieurs cartes uniques avec leur propre design, logo et contenu"
  }, {
    text: "Code d'intégration (embed)",
    included: true,
    tooltip: "Intégrez votre carte directement sur votre site ou application, offrant ainsi une expérience entièrement personnalisée."
  }, {
    text: "Notifications géolocalisées avancées",
    included: true,
    tooltip: "Déclenchez automatiquement une notification push lorsqu'un abonné entre dans une zone définie."
  }, {
    text: "Tags & segmentation",
    included: true,
    tooltip: "Ciblez des groupes spécifiques d'abonnés avec des notifications personnalisées basées sur vos tags et segments."
  }, {
    text: "Statistiques & analyses détaillées",
    included: true,
    tooltip: "Suivez les performances de vos cartes, installations, taux de clics et résultats de vos diffusions."
  }],
  buttonText: "Continuer avec Pro",
  helperText: "Sans engagement",
  favicon: faviconPro
}];
export const faqItems = [{
  question: "Puis-je annuler à tout moment ?",
  answer: "Oui. Vous pouvez annuler, passer à une offre supérieure ou rétrograder quand vous voulez. Votre abonnement reste actif jusqu'à la fin de la période en cours."
}, {
  question: "Qu'est-ce qu'une installation active ?",
  answer: "Une installation active est un abonné qui a ajouté votre carte à son Wallet et ne l'a pas supprimée. Si quelqu'un retire votre carte, il n'est plus comptabilisé."
}, {
  question: "Puis-je avoir plusieurs cartes sur mon compte ?",
  answer: "Oui. Vous pouvez créer autant de cartes que nécessaire (cartes différentes par projet, par communauté, par site…) sur le plan Pro. Le nombre total d'abonnés compte sur l'ensemble de vos cartes."
}, {
  question: "La géolocalisation fonctionne-t-elle partout ?",
  answer: "Oui. Les notifications géolocalisées fonctionnent dans le monde entier. Vous définissez la zone géographique de votre choix."
}, {
  question: "Qu'arrive-t-il si je dépasse mon nombre d'abonnés ?",
  answer: "L'installation de nouvelles cartes est bloquée dès que vous atteignez votre limite. Vos abonnés existants restent actifs et reçoivent vos notifications. Les installations se réactivent automatiquement quand le nombre descend sous la limite ou quand vous passez au plan supérieur."
}, {
  question: "Mes données sont-elles en sécurité ?",
  answer: "Oui. UNOTIF ne collecte aucune donnée personnelle de vos abonnés (ni nom, ni email, ni localisation). Nous suivons uniquement le nombre de cartes distribuées et de notifications envoyées. Votre audience reste privée, et toute communication est uniquement en mode diffusion."
}, {
  question: "UNOTIF est-il conforme au RGPD ?",
  answer: "Oui. UNOTIF est conforme au RGPD. Nous ne collectons aucune donnée personnelle de vos abonnés et toutes les données sont chiffrées et hébergées en Europe."
}, {
  question: "Proposez-vous des remboursements ?",
  answer: "Non. Mais vous pouvez tester gratuitement toutes les fonctionnalités d'UNOTIF avec l'offre Gratuit avant de passer à un plan payant."
}, {
  question: "Quels moyens de paiement acceptez-vous ?",
  answer: "Nous acceptons toutes les cartes bancaires via notre partenaire de paiement sécurisé Stripe. Les prix sont affichés hors TVA."
}, {
  question: "Comment vous contacter si j'ai besoin d'aide ?",
  answer: "Support disponible 7 jours sur 7. Posez toutes vos questions à notre équipe à n'importe quel moment par email ou chat. Nous vous répondons rapidement pour vous aider à tirer le meilleur d'UNOTIF."
}];
const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);

  // Schémas JSON-LD pour le SEO
  const jsonLdSchemas = useMemo(() => [
    generateWebPageSchema("Tarifs", seoConfig.pricing.description, "/tarifs"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Tarifs", path: "/tarifs" }
    ]),
    generateProductSchema("Gratuit", plans[0].subtitle, 0),
    generateProductSchema("Starter", plans[1].subtitle, plans[1].monthlyPrice),
    generateProductSchema("Pro", plans[2].subtitle, plans[2].monthlyPrice),
    generateFAQSchema(faqItems)
  ], []);

  const handleTooltipToggle = (id: string) => {
    setOpenTooltipId(prev => prev === id ? null : id);
  };

  return <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.pricing} jsonLd={jsonLdSchemas} />
      <Header />

      <TooltipProvider delayDuration={0}>
      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16 lg:pb-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Badge */}
            <div className="inline-block px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155] mb-6">Tarifs et abonnements</div>

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Une tarification{" "}
              <span className="text-[#0C94A6]">adaptée à votre usage</span>
            </h1>

            {/* Subtitle */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-2xl mx-auto">
              Commencez gratuitement, évoluez selon vos besoins.
            </p>
          </div>
        </section>

        {/* Toggle Section */}
        <section className="pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-3">
              <div className="relative inline-flex items-center gap-2 sm:gap-4">
                <span className={`text-sm font-medium transition-colors text-right min-w-[90px] sm:min-w-0 ${!isAnnual ? "text-[#074155]" : "text-muted-foreground"}`}>
                  Facturation mensuelle
                </span>
                <Switch checked={isAnnual} onCheckedChange={setIsAnnual} className="data-[state=checked]:bg-[#0C94A6] shrink-0" />
                <span className={`text-sm font-medium transition-colors text-left min-w-[90px] sm:min-w-0 ${isAnnual ? "text-[#074155]" : "text-muted-foreground"}`}>
                  Facturation annuelle
                </span>
                {isAnnual && (
                  <span className="absolute left-full ml-3 px-3 py-1 rounded-full bg-[#0C94A6]/10 text-[#0C94A6] text-xs font-semibold whitespace-nowrap hidden lg:inline-block">
                    20% d'économies
                  </span>
                )}
              </div>
              {isAnnual && (
                <span className="px-3 py-1 rounded-full bg-[#0C94A6]/10 text-[#0C94A6] text-xs font-semibold lg:hidden">
                  20% d'économies
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-[35px] sm:pb-[45px] lg:pb-[55px] overflow-visible">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 md:w-[60%] md:mx-auto lg:w-full">
              {plans.map((plan, planIndex) => <div key={plan.name} className={`group relative z-10 hover:z-40 rounded-2xl bg-white border shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)] p-6 lg:p-8 transition-[transform,box-shadow] duration-300 transform-gpu will-change-transform hover:-translate-y-3 hover:scale-[1.02] hover:shadow-[0_20px_40px_-8px_rgba(0,0,0,0.15),0_8px_16px_-4px_rgba(0,0,0,0.08)] ${planIndex === 1 ? 'border-[#0c97a9]' : 'border-[#91d0d8]'}`}>
                  {/* Popular Badge */}
                  {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1.5 rounded-full bg-[#0C94A6] text-white text-xs font-semibold whitespace-nowrap">
                        Le plus populaire
                      </span>
                    </div>}

                  {/* Header: Name + Favicon */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-[#074155]">
                        {plan.name}
                      </h3>
                      {/* Price */}
                      <div className="mt-2">
                        {plan.monthlyPrice === 0 ? <>
                            <span className="text-3xl font-bold text-[#074155]">
                              0 €
                            </span>
                            <span className="text-[14px] font-medium text-[#495456] ml-2">
                              {plan.priceLabel}
                            </span>
                          </> : isAnnual ? <>
                            <span className="text-base text-gray-400 line-through mr-2">
                              {plan.monthlyPrice} €
                            </span>
                            <span className="text-3xl font-bold text-[#074155]">
                              {plan.annualPrice} €
                            </span>
                            <span className="text-[14px] font-medium text-[#495456] ml-1">
                              {plan.priceLabel}
                            </span>
                            <p className="text-xs font-medium text-[#495456] mt-1">
                              {plan.annualPriceLabel}
                            </p>
                          </> : <>
                            <span className="text-3xl font-bold text-[#074155]">
                              {plan.monthlyPrice} €
                            </span>
                            <span className="text-[14px] font-medium text-[#495456] ml-1">
                              {plan.priceLabel}
                            </span>
                          </>}
                      </div>
                    </div>
                    <img src={plan.favicon} alt={`${plan.name} favicon`} className="w-14 h-14 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 ml-4" />
                  </div>

                  {/* Subtitle */}
                  <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-6 min-h-[48px]">
                    {plan.subtitle}
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, index) => <li key={index} className="flex items-start gap-3">
                        {feature.included ? <Check className="w-5 h-5 text-[#0c97a9] flex-shrink-0 mt-0.5" /> : <X className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />}
                        <span className={`text-[14px] font-medium ${feature.included ? "text-[#022226]" : "text-gray-400"}`}>
                          {feature.text}
                        </span>
                        {feature.tooltip && (() => {
                          const tooltipId = `${planIndex}-${index}`;
                          return (
                            <Tooltip 
                              open={openTooltipId === tooltipId} 
                              onOpenChange={(open) => setOpenTooltipId(open ? tooltipId : null)}
                            >
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => handleTooltipToggle(tooltipId)}
                                  className="focus:outline-none"
                                >
                                  <Info className="w-4 h-4 text-gray-400 flex-shrink-0 cursor-help mt-0.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs z-[100]">
                                <p>{feature.tooltip}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })()}
                      </li>)}
                  </ul>

                  {/* Button */}
                  <Button asChild className="w-full bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      {plan.buttonText}
                    </a>
                  </Button>

                  {/* Helper Text */}
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    {plan.helperText}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="pt-[35px] sm:pt-[45px] lg:pt-[55px] pb-20 sm:pb-28 lg:pb-36 bg-[#F8F9FA]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Title */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] text-center mb-4">
              Questions fréquentes
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] text-center mb-12">
              Tout ce qu'il faut savoir sur <span className="font-semibold text-[#0c97a9]">Unotif</span> et nos offres.
            </p>

            {/* Accordion */}
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]">
              <Accordion type="single" collapsible>
                {faqItems.map((item, index) => <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-100 last:border-b-0">
                    <AccordionTrigger className="text-left text-[#022226] hover:no-underline py-4 text-base font-semibold">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] pb-4">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>
        </section>
      </main>
      </TooltipProvider>

      <Footer />
    </div>;
};
export default Pricing;